import React, { useState, useEffect, useContext, ChangeEvent } from "react";
import { useNavigate } from 'react-router-dom';
import { Stack, StackItem } from '@fluentui/react'
import { FluentProvider, webLightTheme, Image, Input, Button, Badge, Spinner, LargeTitle, Body1, Body2, Body1Strong, Text } from '@fluentui/react-components'
import { backgroundPosition, DynamicBoxingCSS, fitcontentWidth, halfWidth, logoCSS, parentHome, sideImageCSS, signLogo, verifyotpFormPosition, dashboardTheme } from "../../styles/customStyles";
import { SkeletonSignin } from "../../components/skeleton/skeletonSignin";
import { clearLoginAttemptInfo, consumeCode, createCode } from "supertokens-web-js/recipe/passwordless";
import { handleResend } from "../../api/sing_in/otp_func";
import { GlobalContext } from "../../hooks/globalContext";

function Verifyotp() {
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(true);
  const [otp, setOtp] = useState<string[]>(Array(6).fill('')); // State to store OTP input
  const [minutes, setMinutes] = useState(1);
  const [seconds, setSeconds] = useState(60);
  const [isLoading, setIsLoading] = useState(false);
  const [messageBar, setMessageBar] = useState('');
  const [error, setError] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const contextValue = useContext(GlobalContext);
  const user_email = contextValue?.userSessionDetails.userEmail;

  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }

      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(interval);
        } else {
          setSeconds(59);
          setMinutes(minutes - 1);
        }
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [seconds]);

  async function handleOTPInput(otp: string) {
    setIsLoading(true);
    try {
      let response = await consumeCode({
        userInputCode: otp
      });
      console.log('response from consumeCode called', response);
      return response;
    } catch (err: any) {
      setIsLoading(false);
      if (err.isSuperTokensGeneralError === true) {
        // this may be a custom error message sent from the API by you.
        setError(true);
        setMessageBar(err.message);
        setTimeout(() => {
          handleRedirection();
        }, 1000);
      } else if (err.message == "unauthorised") {
        setError(true);
        setMessageBar("Session expired, please try again.");
        setTimeout(() => {
          handleRedirection();
        }, 1000);
      } else {
        setError(true);
        setMessageBar("Session expired, please try again.");
        setTimeout(() => {
          handleRedirection();
        }, 1000);
      }
    }
  }

  useEffect(() => {
    // localStorage.clear();
    const checkConnection = async () => {
      const hasInternetConnection = navigator.onLine;
      setIsOnline(hasInternetConnection);
      //await Session.signOut(); 
    };
    checkConnection();
    window.addEventListener('online', checkConnection);
    window.addEventListener('offline', checkConnection);
    return () => {
      window.removeEventListener('online', checkConnection);
      window.removeEventListener('offline', checkConnection);
    };
  }, []);

  // Handle OTP input change
  const handleOtpChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp${index + 1}`) as HTMLInputElement;
        nextInput?.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp${index - 1}`) as HTMLInputElement;
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = e.clipboardData.getData('text');
    if (/^\d{6}$/.test(pasteData)) {
      const newOtp = pasteData.split('');
      setOtp(newOtp);
      document.getElementById(`otp5`)?.focus();
    }
    e.preventDefault();
  };

  const handleResendOtp = async () => {
    setDisabled(false);
    setError(false);
    setMessageBar("");
    setOtp(Array(6).fill(''));
    //setIsLoading(true);
    setMinutes(1);
    setSeconds(60);
    const response: any = handleResend();

    if (response.status === "RESTART_FLOW_ERROR") {
      // this can happen if the user has already successfully logged in into
      // another device whilst also trying to login to this one.

      // we clear the login attempt info that was added when the createCode function
      // was called - so that if the user does a page reload, they will now see the
      // enter email / phone UI again.
      await clearLoginAttemptInfo();
      setError(true);
      setMessageBar("Login failed. Please try again");
      setTimeout(() => {
        handleRedirection();
      }, 1000);
      // return response.status;
    } else {
      // OTP resent successfully.
      console.log("Please check your email for the new OTP");
      setError(true);
      setMessageBar("Please check your email for the new OTP");
    }
  };

  // Function to handle OTP submission
  const handleOtpSubmit = async () => {
    setDisabled(true);
    setError(false);
    setMessageBar("");
    // setIsLoading(true);
    // e.preventDefault(); // Prevent default form submission
    // setErrorMessage(''); // Clear previous error messages
    if (otp.includes('')) {
      setError(true);
      setMessageBar('Please enter all 6 digits.');
      setDisabled(false);
      return;
    }
    console.log('otp', otp);
    try {
      let response: any = await handleOTPInput(otp.join('')); // Call your imported OTP handler function
      console.log('OTP verify response: ', response)
      if (response.status === "OK") {
        // we clear the login attempt info that was added when the createCode function 
        // was called since the login was successful.
        await clearLoginAttemptInfo();
        if (response.createdNewRecipeUser && response.user.loginMethods.length === 1) {
          // user sign up success
        } else {
          // user sign in success
        }
        setIsLoading(false);
        let userDetails: any = sessionStorage.getItem('userDetails');
        localStorage.setItem('userDetails', userDetails);
        navigate("/home")
      } else if (response.status === "INCORRECT_USER_INPUT_CODE_ERROR") {
        setDisabled(false);
        setIsLoading(false);
        // the user entered an invalid OTP
        setError(true);
        setMessageBar("Wrong OTP! Please try again. Number of attempts left: " + (response.maximumCodeInputAttempts - response.failedCodeInputAttemptCount));
      } else if (response.status === "EXPIRED_USER_INPUT_CODE_ERROR") {
        setDisabled(false);
        setIsLoading(false);
        // it can come here if the entered OTP was correct, but has expired because
        // it was generated too long ago.
        setError(true);
        setMessageBar("Old OTP entered. Please regenerate a new one and try again");
      } else {
        setDisabled(false);
        setIsLoading(false);
        // this can happen if the user tried an incorrect OTP too many times.
        // or if it was denied due to security reasons in case of automatic account linking

        // we clear the login attempt info that was added when the createCode function
        // was called - so that if the user does a page reload, they will now see the
        // enter email / phone UI again.
        await clearLoginAttemptInfo();
        setError(true);
        setMessageBar("Login failed. Please try again");
        setTimeout(() => {
          handleRedirection()
        }, 1000);
      }
    } catch (err) {
      // Handle errors, if necessary, based on exceptions thrown by `handleOTPInput`
      setError(true);
      setDisabled(false);
      setMessageBar('Error processing OTP. Please try again.');
    }
  };

  const handleRedirection = () => {
    navigate("/");
  };

  return (
    <>
      {isOnline ? (
        <FluentProvider style={parentHome} theme={dashboardTheme}>
          <Stack horizontal style={parentHome}>
            <Stack style={halfWidth} horizontalAlign="start">
              <StackItem style={logoCSS}>
                <Stack style={fitcontentWidth} tokens={{ padding: 10 }}>
                  <Image src="/Dashboard/image/signinLogo.svg" />
                </Stack>
              </StackItem>
              <Stack grow style={DynamicBoxingCSS}>
                <Stack tokens={{ childrenGap: 80 }} style={DynamicBoxingCSS}>
                  <Stack style={DynamicBoxingCSS} verticalAlign='center' horizontalAlign='center'>
                    <Stack tokens={{ childrenGap: 15, padding: '20px 150px' }} >
                      <LargeTitle> Sign in </LargeTitle>
                      <Body1 align='justify' wrap={true}> We've sent a One-Time Password (OTP) to your email address <b>{user_email}</b>. Please enter it below to verify your identity. </Body1>
                      <StackItem align="center">
                        {seconds > 0 || minutes > 0 ? (
                          <Text>{minutes < 10 ? `0${minutes}` : minutes}:
                            {seconds < 10 ? `0${seconds}` : seconds}</Text>
                        ) : (
                          <Text></Text>
                        )}

                      </StackItem>
                      <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="center">
                        {otp.map((digit, index) => (
                          <Input
                            key={index}
                            type="text"
                            id={`otp${index}`}
                            name={`otp${index}`}
                            value={digit}
                            onChange={(e) => handleOtpChange(e, index)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            onPaste={handlePaste}
                            maxLength={1}
                            style={{ width: '35px', textAlign: 'center' }}
                            autoComplete="off"
                          />
                        ))}
                      </Stack>
                      <Stack horizontal tokens={{ childrenGap: 25 }} horizontalAlign="center">
                        <StackItem>
                          <Button shape="circular"
                            disabled={seconds > 0 || minutes > 0}
                            onClick={handleResendOtp}
                          >
                            <Body1Strong> Resend OTP </Body1Strong>
                          </Button>
                        </StackItem>
                        <StackItem>
                          <Button shape="circular" appearance='primary' onClick={handleOtpSubmit} disabled={disabled}>
                            <Body1Strong> Verify OTP </Body1Strong>
                          </Button>
                        </StackItem>
                      </Stack>
                      <Stack horizontalAlign="center" tokens={{ childrenGap: 15 }} >
                        <StackItem>
                          {error && <Badge appearance="ghost" color="danger"><Body2> {messageBar} </Body2></Badge>}
                          <Stack horizontalAlign='start'>{isLoading && <Spinner delay={1000} size='extra-tiny' label="Please wait...." />}</Stack>
                        </StackItem>
                      </Stack>
                      <Stack horizontalAlign="center" tokens={{ childrenGap: 20 }}>
                        <StackItem>
                          <Button shape="circular" onClick={handleRedirection}>
                            <Body1Strong> Login with another account </Body1Strong>
                          </Button>
                        </StackItem>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack style={sideImageCSS}>
              <Stack grow>
                <Image block src="/Dashboard/signin_background.png" height={`100%`} />
              </Stack>

            </Stack>
          </Stack>
        </FluentProvider>
      ) : (
        <SkeletonSignin />
      )}
    </>
  );
}

export default Verifyotp
